<script setup>
import { useLayout } from '@/layout/composables/layout';
import { computed, ref, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useUserSession } from "@/stores/userSession";
import { storeToRefs } from "pinia";
import axios from 'axios';
import config from '@/config';

const { toggleMenu, layoutConfig, tabs, closeTab, isDarkTheme, toggleConfigSidebar } = useLayout();

const userSession = useUserSession();
const { user, getActiveBreadcrumb } = storeToRefs(userSession);

const oldCapMagicUrl = import.meta.env.VITE_OLD_CAPMAGIC_LOCAL_URL;
const oldCapMagicUrlStage = import.meta.env.VITE_OLD_CAPMAGIC_STAGE_URL;
const oldCapMagicUrlProd = import.meta.env.VITE_OLD_CAPMAGIC_PROD_URL;

const router = useRouter();

function onCloseTab(index) {
    if (tabs.value.length > 1) {
        if (index === tabs.value.length - 1) router.push(tabs.value[tabs.value.length - 2].to);
        else router.push(tabs.value[index + 1].to);
    } else {
        router.push('/');
    }
    closeTab(index);
}

const logo = computed(() => {
    const path = '/layout/images/logo-';

    let logo;
    if (layoutConfig.layoutTheme === 'primaryColor' && layoutConfig.theme !== 'yellow') {
        logo = 'light.png';
    } else {
        logo = isDarkTheme.value ? 'light.png' : 'dark.png';
    }
    return path + logo;
});

const isLoggingOut = ref(false);
const handleLogout = async () => {
  try {
    isLoggingOut.value = true;
    const baseURL = import.meta.env.VITE_API_BASE_URL;
    await axios.post(`${baseURL}/logout`, null, {
      headers: {
        Authorization: userSession.token
      }
    });
  } catch (error) {
    console.error('Logout API call failed:', error);
  } finally {
    userSession.logoutUser();
    router.push("/login");
    isLoggingOut.value = false;
  }
};

const route = useRoute();
const home = { icon: 'pi pi-home', to: '/' };

const breadcrumbRoutes = ref([]);

const setBreadcrumbRoutes = () => {
  if (route.meta.breadcrumb) {
    breadcrumbRoutes.value = route.meta.breadcrumb;
    return;
  }

  breadcrumbRoutes.value = route.fullPath
    .split('/')
    .filter((item) => item !== '')
    .filter((item) => isNaN(Number(item)))
    .map((item) => item.charAt(0).toUpperCase() + item.slice(1));
};

watch(
  route,
  () => {
    setBreadcrumbRoutes();
  },
  { immediate: true }
);

function getInitialFromName(name) {
  return name.split(' ').map(word => word[0]).join('');
}

function currentHostname() {
  return window.location.hostname.toLowerCase();
}

const showSwitchURL = (name = 'local') => {
  // if (user.value.userType != config.userType.admin) {
  //   return false;
  // }

  const host = currentHostname();

  if (name === 'local') {
    return host === 'localhost' || host === '127.0.0.1';
  }

  if (name === 'stage') {
    if (host === 'stage.capmagic.net') return false;
    if (host === 'dev.capmagic.net') return true;
    return true;
  }

  if (name === 'prod') {
    if (host === 'stage.capmagic.net' || host === 'dev.capmagic.net') return true;
    return true;
  }

  return false;
};

function switchToLocal(name = 'local') {
  const accessToken = userSession.token;
  const laravelJwt = accessToken;
  //  get current active route
  const currentRoute = route.name;
  const prefix = name === 'local' ? oldCapMagicUrl : name === 'stage' ? oldCapMagicUrlStage : oldCapMagicUrlProd;
  let newCapMagicUrl = '';
  if (name === 'local') {
    newCapMagicUrl = prefix + '/sso-verify/?token=' + encodeURIComponent(laravelJwt) + '&redirectTo=' + currentRoute;
  } else if (name === 'stage') {
    newCapMagicUrl = prefix + '/saml/acs-verify-token?token=' + encodeURIComponent(laravelJwt) + '&redirectTo=' + currentRoute;
  } else if (name === 'prod') {
    newCapMagicUrl = prefix + '/sso-verify/?token=' + encodeURIComponent(laravelJwt) + '&redirectTo=' + currentRoute;
  } else {
    console.log('Invalid URL');
    return;
  }

  window.open(newCapMagicUrl, '_blank', 'noopener,noreferrer');
}
</script>

<template>
    <div class="layout-topbar">
      <router-link to="/" class="app-logo">
        <img alt="app logo" :src="logo" />
        <span class="app-name">CapMagic</span>
      </router-link>

      <button ref="menubutton" class="topbar-menubutton p-link" type="button" @click="toggleMenu">
        <span></span>
      </button>
      <div class="flex w-full justify-between items-center mr-4 ml-14 !font-bold">
        <nav class="layout-breadcrumb">
          <ol>
            <li>
              <router-link :to="home.to" style="color: inherit">
                <em :class="home.icon"></em>
              </router-link>
            </li>
            <li class="layout-breadcrumb-chevron">/</li>
            <template v-for="(breadcrumbRoute, i) in breadcrumbRoutes" :key="breadcrumbRoute">

              <li v-if="breadcrumbRoutes.length - 1 != i" class="flex items-center !font-bold">
                <!-- set breadcrumb icon -->
                <!-- <i v-if="!!breadcrumbRoute.icon" :class="breadcrumbRoute.icon" class="mr-2"></i> -->
                <em v-if="!!breadcrumbRoute.icon" :class="breadcrumbRoute.icon" class="mr-2"></em>
                <router-link :to="{ name: breadcrumbRoute.name }">
                  {{ breadcrumbRoute.title }}
                </router-link>
              </li>
              <!-- make sure always clean title when leaving (on unmounted else default value will not work) -->
              <!-- show title from store if not avilable then display normal title if not set then also fallback to below title -->
              <li v-else-if="!!getActiveBreadcrumb">{{ getActiveBreadcrumb }}</li>
              <li v-else class="flex items-center !font-bold">
                <!-- set breadcrumb icon -->
                <!-- <i v-if="!!breadcrumbRoute.icon" :class="breadcrumbRoute.icon" class="mr-2"></i> -->
                <!-- <em v-if="!!breadcrumbRoute.icon" :class="breadcrumbRoute.icon" class="mr-2"></em> -->
                <ThemedIcon :name="breadcrumbRoute.name" v-if="!!breadcrumbRoute.icon" isTopBar/>
                <span class="ml-2">{{ breadcrumbRoute.title }}</span>
              </li>

              <li v-if="i !== breadcrumbRoutes.length - 1" class="layout-breadcrumb-chevron">/</li>
            </template>
          </ol>
        </nav>
        <div class="topbar-profile flex">
            <Button severity="contrast" rounded type="button" class="mt-1 mr-1" icon="pi pi-palette" @click="toggleConfigSidebar" />

          <button class="topbar-profile-button ml-2" type="button" v-styleclass="{ selector: '@next', enterFromClass: 'hidden', enterActiveClass: 'animate-scalein', leaveToClass: 'hidden', leaveActiveClass: 'animate-fadeout', hideOnOutsideClick: true }">
            <!-- <img alt="avatar" src="/layout/images/avatar.png" /> -->
            <Avatar :label="getInitialFromName(user.fullName)" size="large" shape="circle" />
            <span class="profile-details ml-2">
              <span class="profile-name">{{ user.fullName }}</span>
              <!-- <span class="profile-job">{{ user.email }}</span> -->
            </span>
            <em class="pi pi-angle-down"></em>
          </button>
          <ul
            class="list-none p-3 m-0 rounded-lg shadow-lg hidden absolute bg-white border w-full sm:w-48 mt-2 right-0 top-full z-50">
            <!-- <li v-if="showSwitchURL('prod')">
              <a v-ripple target="_blank" @click="switchToLocal('prod')" v-tooltip.top="oldCapMagicUrlProd"
                class="p-ripple flex p-2 rounded-lg items-center hover:bg-gray-100 transition cursor-pointer">
                <em class="pi pi-external-link mr-3"></em>
                <span>Switch to Old CapMagic</span>
              </a>
            </li>
            <li v-if="showSwitchURL('stage')">
              <a v-ripple target="_blank" @click="switchToLocal('stage')" v-tooltip.top="oldCapMagicUrlStage"
                class="p-ripple flex p-2 rounded-lg items-center hover:bg-gray-100 transition cursor-pointer">
                <em class="pi pi-external-link mr-3"></em>
                <span>Switch to Stage CapMagic</span>
              </a>
            </li>
            <li v-if="showSwitchURL('local')">
              <a v-ripple target="_blank" @click="switchToLocal('local')" v-tooltip.top="oldCapMagicUrl"
                class="p-ripple flex p-2 rounded-lg items-center hover:bg-gray-100 transition cursor-pointer">
                <em class="pi pi-external-link mr-3"></em>
                <span>Switch to Old CapMagic(Local)</span>
              </a>
            </li> -->
            <li>
              <a v-ripple @click="handleLogout"
                class="p-ripple flex p-2 rounded-lg items-center hover:bg-gray-100 transition cursor-pointer">
                <em class="pi pi-power-off mr-3"></em>
                <span>Sign Out</span>
              </a>
            </li>
          </ul>
        </div>
      </div>
      <Dialog :draggable="false" :position="'center'" v-model:visible="isLoggingOut"
          modal :header="undefined" class="!bg-primary !border-none !rounded-none dialog-enlarged !w-96"
          :breakpoints="{ '1199px': '75vw', '575px': '90vw' }" :pt="{
              header: {
                  class: '!hidden'
              }
          }">
          <div class="my-5">
              <p class="capitalize my-5 font-bold text-white">
                  Logging out, Please wait...
              </p>
              <ProgressBar mode="indeterminate" style="height: 6px"></ProgressBar>
          </div>
      </Dialog>
    </div>
  </template>
